import { useRecoilState, useSetRecoilState } from 'recoil';
import { userAuthAtom, userDataErrorAtom } from './atoms';
import { useNavigate } from 'react-router-dom';
import { Paths } from 'services/navigation/paths';
import { logger } from 'core/utils/logger';
import { CognitoHostedUIIdentityProvider } from './types';
import { useUserFetcher } from 'services/user/hooks';
import { useState } from 'react';
import { authService, AuthUser } from './authService';
import { isSafeInternalPath } from './utils/ssoRedirect';
import {
  buildSsoLogoutUrl,
  PendingSso,
  savePendingSso
} from './utils/ssoReauth';

export const useAuth = () => {
  const navigate = useNavigate();
  const [userAuth, setUserAuth] = useRecoilState(userAuthAtom);
  const setUserDataError = useSetRecoilState(userDataErrorAtom);
  const { fetchUserData, clearUserData } = useUserFetcher();
  const [loading, setLoading] = useState(false);

  const signIn = async (email: string, password: string) => {
    try {
      setUserDataError(false);
      const newUser = await authService.signIn(email, password);
      setUserAuth({
        username: newUser.username,
        attributes: newUser.attributes
      });
      try {
        await fetchUserData(newUser.attributes.sub);
      } catch (error) {
        // Sign-in succeeded but the user data did not load. Keep the session
        // and let the route guards offer a retry, mirroring checkUser.
        logger.error(error, 'Error fetching user data');
        clearUserData();
        setUserDataError(true);
      }
      return newUser;
    } catch (error) {
      logger.error(error, 'Error signing in');
      throw error;
    }
  };

  const federatedSignIn = async ({
    provider,
    customState
  }: {
    provider: CognitoHostedUIIdentityProvider | string;
    customState?: string;
  }) => {
    return authService.federatedSignIn({ provider, customState });
  };

  // A ?sso=<provider> deep-link must authenticate against that provider rather
  // than be satisfied by whoever is already signed in, so tear the session down
  // first and resume the SSO flow when the logout chain returns to /login.
  const startSsoLogin = async ({ provider, next }: PendingSso) => {
    const logoutUrl = buildSsoLogoutUrl(provider);
    if (!logoutUrl) {
      return federatedSignIn({ provider, customState: next });
    }

    try {
      await authService.signOut();
    } catch (error) {
      logger.error(error, 'Sign-out before SSO failed; continuing to log out');
    }
    clearUserData();
    setUserAuth(null);
    localStorage.clear();
    sessionStorage.clear();

    savePendingSso({ provider, next });
    window.location.assign(logoutUrl);
  };
  const checkUser = async () => {
    setLoading(true);
    setUserDataError(false);
    try {
      let user: AuthUser;
      try {
        user = await authService.currentAuthenticatedUser();
      } catch (error) {
        logger.error(error, 'Error checking user');
        setUserAuth(null);
        clearUserData();
        return;
      }

      const newUser = {
        username: user.username,
        attributes: user.attributes
      };

      setUserAuth(newUser);
      try {
        await fetchUserData(newUser.attributes.sub);
      } catch (error) {
        // Keep the valid session and let route guards offer a retry.
        logger.error(error, 'Error fetching user data');
        // Do not retain tenant state from a previous session.
        clearUserData();
        setUserDataError(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const forgotPassword = async (email: string) => {
    try {
      await authService.forgotPassword(email);
    } catch (err) {
      logger.error(err);
      throw err;
    }
  };

  const resetPassword = async (
    email: string,
    code: string,
    newPassword: string
  ) => {
    try {
      await authService.forgotPasswordSubmit(email, code, newPassword);
    } catch (err) {
      logger.error(err);
      throw err;
    }
  };

  const signOut = async (redirectTo?: string) => {
    try {
      localStorage.clear();
      sessionStorage.clear();
      clearUserData();
      setUserAuth(null);
      await authService.signOut();
      navigate(isSafeInternalPath(redirectTo) ? redirectTo : Paths.LOGIN, {
        replace: true
      });
    } catch (error) {
      logger.error(error, 'Error signing out');
      throw error;
    }
  };

  const signInWithTemPassword = async (email: string, password: string) => {
    try {
      const user = await authService.signIn(email, password);
      return user;
    } catch (error) {
      logger.error(error, 'Error signing in');
      throw error;
    }
  };

  const completeNewPassword = async (
    userParam: AuthUser,
    newPassword: string
  ) => {
    try {
      const user = await authService.completeNewPassword(
        userParam.username,
        newPassword
      );

      setUserAuth({
        username: user.username,
        attributes: user.attributes
      });

      await checkUser();
      navigate(Paths.ONBOARDING);
      return user;
    } catch (error) {
      logger.error(error, 'Error creating password');
      throw error;
    }
  };

  return {
    signIn,
    federatedSignIn,
    startSsoLogin,
    forgotPassword,
    resetPassword,
    signOut,
    signInWithTemPassword,
    completeNewPassword,
    userAuth,
    checkUser,
    isLoading: loading
  };
};
